import React, { useState, useEffect } from 'react';
import { CarData } from '../CarInfo/CarInfo';

import './DisplayScreen.css';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Box } from '@mui/material';

export type DisplayState = 'READY' | 'Lora OBD detected';

interface DisplayScreenProps {
  state: DisplayState;
  carData?: CarData | null;
  isProcessing?: boolean;
  className?: string;
}

const DisplayScreen: React.FC<DisplayScreenProps> = ({
  state,
  carData,
  className = '',
}) => {
  // const [progress, setProgress] = useState(0);
  // const [isTimerActive, setIsTimerActive] = useState(false);
  const [isScanned, setIsScanned] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // const TIMER_DISPLAY_DURATION = 5000;
  const ANIMATION_SWITCH_DELAY = 2000;

  useEffect(() => {
    if (state === 'Lora OBD detected') {
      // Reset to first animation when new vehicle is detected
      setIsScanned(false);
      setIsTransitioning(false);

      // Switch to second animation after 2 seconds
      const animationTimer = setTimeout(() => {
        setIsTransitioning(true);
        // Small delay to ensure smooth transition
        setTimeout(() => {
          setIsScanned(true);
          setIsTransitioning(false);
        }, 50);
      }, ANIMATION_SWITCH_DELAY);

      return () => clearTimeout(animationTimer);
    } else {
      // Reset animation state when in READY state
      setIsScanned(false);
      setIsTransitioning(false);
    }
  }, [state, carData?.ble_identifier]);

  const getStateColor = (state: DisplayState): string => {
    switch (state) {
      case 'READY':
        return '#14b8a6';
      case 'Lora OBD detected':
        return '#dc2626';
      default:
        return '#14b8a6';
    }
  };

  const getStateMessage = (state: DisplayState): string => {
    switch (state) {
      case 'READY':
        return 'READY';
      case 'Lora OBD detected':
        return 'STOP';
      default:
        return 'READY';
    }
  };

  return (
    <div
      className={`display-screen ${className}`}
      style={{ backgroundColor: getStateColor(state) }}
      data-testid="display-screen"
    >
      <div className="message" style={{ position: 'relative' }}>
        {state !== 'READY' && !isTransitioning && (
          <Box
            sx={{
              position: 'absolute',
              top: isScanned ? '-200px' : '-160px',
              right: '0px',
            }}
          >
            {!isScanned ? (
              <DotLottieReact
                key={`scanning-${carData?.vin || 'default'}`}
                src="https://lottie.host/81b66fd8-5354-47ca-a92a-22f8cd67af4e/lFQCXd7Xww.lottie"
                loop={false}
                autoplay
                style={{
                  width: '390px',
                  height: '140px',
                }}
              />
            ) : (
              <DotLottieReact
                key={`scanned-${carData?.vin || 'default'}`}
                src="https://lottie.host/da2c0649-dad0-4114-b945-e9fa1ed69884/pgd7V8dXV2.lottie"
                loop
                autoplay
                style={{
                  width: '390px',
                  height: '209px',
                }}
              />
            )}
          </Box>
        )}
        <div data-testid="state-message">
          <h2>{getStateMessage(state)}</h2>
          {state === 'Lora OBD detected' && (
            <div className="remove-tracker-text">Remove Tracker</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DisplayScreen;
